import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { forwardRef, RefObject } from 'react';

import { RouteButton } from '@/components/RouteButton/RouteButton';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { addImageParams, AddImageParamTypes, getCategoryPageUrl, imagePlaceholder } from '@/utils/urls';

import { CategoryNamesAndIds } from '../../constants/constant';
import { Category } from '../../types/CategoryTypes';
import { urlToSend } from '../utils/urlToSend';

export type DiscoverCategoriesSubcategoryProps = {
    category: Category[];
    title: string;
};

const SubCategoryInner = ({ category, title }: DiscoverCategoriesSubcategoryProps, ref?: RefObject<HTMLDivElement>) => {
    const formatMessage = useAppFormatMessage();
    return (
        <div
            className="max-[990px]:ml-[10px] mt-[55px] xsMax:ml-[66px]"
            ref={ref}
        >
            <div className="max-[990px]:w-[88%] max-[990px]:[&_h2]:ml-0 mt-[40px] flex w-[89%] justify-between xsMax:w-[84.7%] [&_h2]:ml-[32px]">
                <H2>{formatMessage(['discoverPage', 'subCategory', 'subCategoryTitle'], { values: { title } })}</H2>
                <RouteButton
                    size="sm"
                    suppressHydrationWarning
                    to={getCategoryPageUrl(urlToSend(title.toLocaleLowerCase()), `${CategoryNamesAndIds[title]}`)}
                >
                    {formatMessage(['discoverPage', 'subCategory', 'subCategoryLink'], { values: { title } })}
                </RouteButton>
            </div>
            <div className="max-[990px]:ml-0 ml-[32px] flex flex-row flex-wrap pt-md">
                {category.map((subCategory: Category, index: number) => {
                    const imageParams: AddImageParamTypes = {
                        height: 184,
                        url: subCategory.imageUrl || imagePlaceholder,
                    };

                    return (
                        <div
                            className="max-[990px]:mr-[25px] max-[990px]:w-[178px] mr-[19.5px] max-w-[184px] xsMax:mr-[26px]"
                            key={index}
                        >
                            <RouteLink
                                className="block h-full w-full no-underline"
                                to={subCategory.pageUrl}
                            >
                                <div className="my-[20px] flex flex-col items-center justify-center text-left hover:cursor-pointer [&:hover_span]:text-primary-400 [&:hover_span]:underline">
                                    <div className="relative mb-[5px] flex h-[184px] w-[184px] items-center justify-center rounded-[5px]">
                                        <img
                                            alt={subCategory.title}
                                            className="max-h-full max-w-full rounded-sm"
                                            loading={index < 8 ? 'eager' : 'lazy'}
                                            src={addImageParams(imageParams)}
                                        />
                                    </div>
                                    <div className="flex flex-col [&:hover_span]:text-primary-400 [&:hover_span]:underline">
                                        <BodyPrimary>
                                            {formatMessage(['discoverPage', 'subCategory', 'subCategoryTitleSpan'], {
                                                values: { title: subCategory.title },
                                            })}
                                        </BodyPrimary>
                                        {subCategory.followers > 0 && (
                                            <BodyPrimary color="secondary">
                                                {subCategory.followers === 1
                                                    ? formatMessage([
                                                          'discoverPage',
                                                          'subCategory',
                                                          'subCategoryFollower',
                                                      ])
                                                    : formatMessage(
                                                          ['discoverPage', 'subCategory', 'subCategoryFollowers'],
                                                          {
                                                              values: {
                                                                  followers: (
                                                                      <FormattedNumber
                                                                          key="subcategory_followers"
                                                                          value={subCategory.followers}
                                                                      />
                                                                  ),
                                                              },
                                                          }
                                                      )}
                                            </BodyPrimary>
                                        )}
                                    </div>
                                </div>
                            </RouteLink>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const DiscoverCategoriesSubcategory = forwardRef(SubCategoryInner);
